import { api } from './api';
import { 
  Channel, 
  Video, 
  ApiResponse, 
  PaginatedResponse 
} from '@meradio/shared-types';

export interface ChannelSearchParams {
  q: string;
  maxResults?: number;
}

export const searchChannels = async (
  query: string, 
  maxResults: number = 25
): Promise<Channel[]> => {
  const response = await api.get<Channel[]>('/youtube/search/channels', {
    params: { q: query, maxResults }
  });
  
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to search channels');
  }
  
  return response.data;
};

export const getChannelMetadata = async (channelId: string): Promise<Channel> => {
  if (!channelId || channelId.trim().length === 0) {
    throw new Error('Channel ID is required');
  }

  const response = await api.get<Channel>(`/youtube/channels/${channelId}/metadata`);
  
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get channel metadata');
  }
  
  return response.data;
};

export const triggerManualImport = async (channelId: string): Promise<void> => {
  if (!channelId || channelId.trim().length === 0) {
    throw new Error('Channel ID is required');
  }

  const response = await api.post(`/youtube/channels/${channelId}/import`);
  
  if (!response.success) {
    throw new Error(response.error || 'Failed to trigger manual import');
  }
};

export enum YouTubeAPIError {
  INVALID_CHANNEL_ID = 'INVALID_CHANNEL_ID',
  CHANNEL_NOT_FOUND = 'CHANNEL_NOT_FOUND',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  API_QUOTA_EXCEEDED = 'API_QUOTA_EXCEEDED',
  ALREADY_FOLLOWING = 'ALREADY_FOLLOWING',
  NOT_FOLLOWING = 'NOT_FOLLOWING',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export const parseYouTubeError = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  
  const message = error.message || error.error || String(error);
  
  if (message.includes('INVALID_CHANNEL_ID')) {
    return 'Invalid YouTube channel ID';
  }
  if (message.includes('NOT_FOUND')) {
    return 'Channel not found';
  }
  if (message.includes('RATE_LIMIT')) {
    return 'Too many requests. Please try again later.';
  }
  if (message.includes('QUOTA')) {
    return 'YouTube API quota exceeded. Please try again later.';
  }
  if (message.includes('ALREADY_FOLLOWING')) {
    return 'You are already following this channel';
  }
  if (message.includes('NOT_FOLLOWING')) {
    return 'You are not following this channel';
  }
  if (message.includes('Network Error') || message.includes('ECONNABORTED')) {
    return 'Network error. Please check your connection.';
  }
  
  return message || 'Something went wrong. Please try again.';
};
